/* eslint-disable @heidi/rules/no-unguarded-apple-billing -- useSubscriptionUpgrade guards execution internally via isAppleCompliantBuild(); this hook renders no UI */
import { PaywallSource, track, TrackingEventName } from '@heidi/analytics/events';
import { SubscriptionTier, UserActivityAction } from '@heidi/schema/ml-scribe';
import {
  useCombinedQueriesTeamGeneralInfo,
  useUserActivityActionCount,
} from '@heidi/workspace/data';
import { useCallback, useEffect } from 'react';
import { type Period } from '../../../contracts';
import { useReverseTrial } from '../../../data';
import { getCommerceBillingUiPorts } from '../../settings/billing/billing-ui-ports';
import { useSubscriptionUpgrade } from '../../settings/billing/hooks/useSubscriptionUpgrade';
import { getCommerceTrialsUiPorts } from '../../trials-and-upgrades/trials-ui-ports';

export interface UseEvidencePowerUserUpgradeModalProps {
  /** When provided, modal is controlled by a launch flow (e.g. triggered from a banner nudge). */
  launchOpen?: boolean;
  onLaunchClose?: () => void;
  /**
   * Optional override for the usage count (e.g. when previewing on /dev/trial-modals).
   * When unset, the hook reads `user_activity.evidence_chat_query` from the user query.
   */
  countOverride?: number;
}

/**
 * Business logic for the Evidence power-user upgrade modal.
 *
 * Target plan: Clinician Pro — reusing `useSubscriptionUpgrade`, same as `ClinicianUpgradeModal`.
 *
 * Count source: `user_activity.evidence_chat_query` on the user payload. After each
 * Evidence-mode send, `trackChatSubmitted` invalidates the user query so the count refetches.
 */
export function useEvidencePowerUserUpgradeModal({
  launchOpen,
  onLaunchClose,
  countOverride,
}: UseEvidencePowerUserUpgradeModalProps = {}) {
  const [isAtomOpen, setIsAtomOpen] =
    getCommerceTrialsUiPorts().useEvidencePowerUserUpgradeModalOpenState();
  const { clinicianTrialDaysLeft } = useReverseTrial();
  const { hasTeam, isTeamStripeManaged, isNonAdminTeamMember, isLoadingTeamDetails } =
    useCombinedQueriesTeamGeneralInfo();

  const isOpen = isAtomOpen || (launchOpen ?? false);

  useEffect(() => {
    if (isOpen) {
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.PaywallViewed,
        props: {
          source: PaywallSource.EvidencePowerUserUpgradeModal,
          target_plan: SubscriptionTier.CLINICIAN_PRO,
          days_left_on_trial: clinicianTrialDaysLeft,
        },
      });
    }
    // Only fire when modal open state changes, not when other props update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsAtomOpen(false);
    onLaunchClose?.();
  }, [setIsAtomOpen, onLaunchClose]);

  const { data: products, catalogueVersion } =
    getCommerceBillingUiPorts().products.useQueryProducts({ heidi2Catalogue: true });
  const clinicianProduct = getCommerceBillingUiPorts().products.getNewProductTierProduct(
    SubscriptionTier.CLINICIAN_PRO,
    products
  );

  const { handleUpgrade, isPending } = useSubscriptionUpgrade({
    onCheckoutComplete: handleClose,
  });

  const handleUpgradeClick = useCallback(
    (period: Period) =>
      void handleUpgrade(
        period,
        SubscriptionTier.CLINICIAN_PRO,
        catalogueVersion === 'v3'
          ? (getCommerceBillingUiPorts().products.getPlanFromPeriod(
              period,
              clinicianProduct
            )?.id ?? null)
          : undefined
      ),
    [catalogueVersion, clinicianProduct, handleUpgrade]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      } else {
        setIsAtomOpen(true);
      }
    },
    [handleClose, setIsAtomOpen]
  );

  const { data: serverEvidenceCount = 0, isPending: isEvidenceCountPending } =
    useUserActivityActionCount(UserActivityAction.evidence_chat_query);
  const evidenceCount = countOverride ?? serverEvidenceCount;
  const isEvidenceCountLoading = countOverride === undefined && isEvidenceCountPending;

  const shouldDisableButtonForNonStripeManagedTeam = hasTeam && !isTeamStripeManaged;
  const isUpgradeDisabledForTeamMember = !isLoadingTeamDetails && isNonAdminTeamMember;
  const isUpgradeDisabled =
    isUpgradeDisabledForTeamMember || shouldDisableButtonForNonStripeManagedTeam;

  return {
    isOpen,
    handleOpenChange,
    handleUpgradeClick,
    isPending,
    clinicianProduct,
    evidenceCount,
    isEvidenceCountLoading,
    isUpgradeDisabled,
    isUpgradeDisabledForTeamMember,
    shouldDisableButtonForNonStripeManagedTeam,
  };
}
